'use client';

import { useEffect, type RefObject } from 'react';

export const DRAG_THRESHOLD_PX = 5;

const suppressClick = (event: MouseEvent) => {
	event.preventDefault();
	event.stopPropagation();
};

/**
 * Swallows the click the browser fires right after a drag ends, so releasing a drag over a card
 * doesn't follow its link. The click is dispatched in the same task as the mouseup/pointerup, so
 * the listener only needs to live until the next macrotask.
 */
export function suppressNextClick(element: HTMLElement): void {
	element.addEventListener('click', suppressClick, true);
	window.setTimeout(() => element.removeEventListener('click', suppressClick, true), 0);
}

/**
 * Links and images are natively draggable, so a mouse drag that starts on a card would otherwise
 * begin an HTML5 drag-and-drop: the browser fires pointercancel and stops sending pointermove, and
 * the track never scrolls.
 */
export const preventNativeDrag = (event: Event) => event.preventDefault();

export default function useDragToScroll(ref: RefObject<HTMLElement | null>, enabled = true): void {
	useEffect(() => {
		const element = ref.current;
		if (!element || !enabled) return;

		let activePointerId: number | null = null;
		let startClientX = 0;
		let lastClientX = 0;
		let furthestMove = 0;
		let dragging = false;

		const capturePointer = (pointerId: number): boolean => {
			try {
				element.setPointerCapture(pointerId);
				return true;
			} catch {
				return false;
			}
		};

		const releasePointer = (pointerId: number): boolean => {
			try {
				if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
				return true;
			} catch {
				return false;
			}
		};

		const updateCursor = () => {
			if (dragging) return;
			element.style.cursor = element.scrollWidth > element.clientWidth ? 'grab' : '';
		};

		const endDragStyles = () => {
			dragging = false;
			element.style.removeProperty('user-select');
			element.style.removeProperty('scroll-behavior');
			// Removing the inline override hands the track back to its `snap-mandatory` class, and the
			// browser re-snaps to the nearest card from wherever the drag left it.
			element.style.removeProperty('scroll-snap-type');
			updateCursor();
		};

		const onPointerDown = (event: PointerEvent) => {
			if (event.pointerType !== 'mouse' || event.button !== 0) return;
			activePointerId = event.pointerId;
			startClientX = event.clientX;
			lastClientX = event.clientX;
			furthestMove = 0;
		};

		const onPointerMove = (event: PointerEvent) => {
			if (activePointerId === null || event.pointerId !== activePointerId) return;

			furthestMove = Math.max(furthestMove, Math.abs(event.clientX - startClientX));
			if (furthestMove <= DRAG_THRESHOLD_PX) return;

			if (!dragging) {
				// A track with nothing to scroll isn't draggable, so a click that wobbled past the
				// threshold stays a click instead of being swallowed as a drag.
				if (element.scrollWidth <= element.clientWidth) {
					activePointerId = null;
					return;
				}
				dragging = true;
				capturePointer(activePointerId);
				element.style.cursor = 'grabbing';
				element.style.userSelect = 'none';
				element.style.scrollBehavior = 'auto';
				// Mandatory snapping re-snaps after every scrollLeft write, so the track would jump
				// between cards instead of following the pointer.
				element.style.scrollSnapType = 'none';
			}
			// Scroll by the incremental movement rather than from a fixed start offset, so a looping
			// track that repositions itself mid-drag isn't yanked back to where the drag began.
			element.scrollLeft -= event.clientX - lastClientX;
			lastClientX = event.clientX;
		};

		const onPointerUp = (event: PointerEvent) => {
			if (activePointerId === null || event.pointerId !== activePointerId) return;

			releasePointer(activePointerId);
			const dragged = furthestMove > DRAG_THRESHOLD_PX;
			activePointerId = null;
			endDragStyles();

			if (dragged) suppressNextClick(element);
		};

		// The track's overflow changes as images load and the viewport resizes, so the grab
		// affordance can't be decided once at mount.
		const resizeObserver =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateCursor);
		resizeObserver?.observe(element);
		Array.from(element.children).forEach(child => resizeObserver?.observe(child));

		updateCursor();
		element.addEventListener('pointerdown', onPointerDown);
		element.addEventListener('pointermove', onPointerMove);
		element.addEventListener('pointerup', onPointerUp);
		element.addEventListener('pointercancel', onPointerUp);
		element.addEventListener('dragstart', preventNativeDrag);

		return () => {
			resizeObserver?.disconnect();
			element.removeEventListener('pointerdown', onPointerDown);
			element.removeEventListener('pointermove', onPointerMove);
			element.removeEventListener('pointerup', onPointerUp);
			element.removeEventListener('pointercancel', onPointerUp);
			element.removeEventListener('dragstart', preventNativeDrag);
			element.removeEventListener('click', suppressClick, true);
			element.style.removeProperty('cursor');
			element.style.removeProperty('user-select');
			element.style.removeProperty('scroll-behavior');
			element.style.removeProperty('scroll-snap-type');
		};
	}, [ref, enabled]);
}
